import { Component, type ReactNode, type ErrorInfo } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
  level?: "page" | "section";
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] Caught error:", error, info);
    this.props.onError?.(error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      if (this.props.level === "section") {
        return <SectionErrorFallback onReset={this.handleReset} />;
      }
      return <PageErrorFallback error={this.state.error} onReset={this.handleReset} />;
    }
    return this.props.children;
  }
}

/* ─── Page-level fallback ─── */
function PageErrorFallback({
  error,
  onReset,
}: {
  error: Error | null;
  onReset: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-white px-4 font-['Cairo']"
    >
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="w-20 h-20 flex items-center justify-center mx-auto bg-red-50 rounded-full mb-6">
          <i className="ri-error-warning-line text-4xl text-red-400" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-3">
          {t("errorBoundary.pageTitle")}
        </h1>
        <p className="text-gray-500 text-sm leading-relaxed mb-6">
          {t("errorBoundary.pageDescription")}
        </p>

        {/* Error detail (dev-friendly) */}
        {error && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6 text-right">
            <p className="text-xs text-gray-400 mb-1 font-semibold uppercase tracking-wider">
              {t("errorBoundary.details")}
            </p>
            <p className="text-xs text-red-500 font-mono break-all leading-relaxed">
              {error.message}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={onReset}
            className="flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A1628] font-bold px-6 py-3 rounded-full hover:bg-[#c49f30] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" />
            {t("errorBoundary.retry")}
          </button>
          <a
            href="/"
            className="flex items-center justify-center gap-2 border border-gray-200 text-gray-600 font-medium px-6 py-3 rounded-full hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line" />
            {t("errorBoundary.home")}
          </a>
        </div>
      </div>
    </div>
  );
}

/* ─── Section-level fallback (inline, less intrusive) ─── */
function SectionErrorFallback({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation();

  return (
    <div
      className="flex flex-col items-center justify-center py-12 px-4 bg-gray-50 rounded-2xl border border-gray-100 font-['Cairo']"
    >
      <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mb-3">
        <i className="ri-error-warning-line text-xl text-red-400" />
      </div>
      <p className="text-gray-600 text-sm font-semibold mb-1">{t("errorBoundary.sectionTitle")}</p>
      <p className="text-gray-400 text-xs mb-4">{t("errorBoundary.sectionDescription")}</p>
      <button
        onClick={onReset}
        className="flex items-center gap-1.5 text-xs text-[#D4AF37] border border-[#D4AF37]/40 px-4 py-2 rounded-full hover:bg-[#D4AF37]/10 transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-refresh-line" />
        {t("errorBoundary.retry")}
      </button>
    </div>
  );
}

/* ─── Convenience wrappers ─── */
export function PageErrorBoundary({ children }: { children: ReactNode }) {
  return <ErrorBoundary level="page">{children}</ErrorBoundary>;
}

export function SectionErrorBoundary({ children }: { children: ReactNode }) {
  return <ErrorBoundary level="section">{children}</ErrorBoundary>;
}

export default ErrorBoundary;
